import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTransferUser } from '../auth/useTransferUser';

interface NavItem {
  to: string;
  label: string;
  visibleFor: ReadonlyArray<'dfx_super_admin' | 'org_admin' | 'org_uploader'>;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Overview', visibleFor: ['dfx_super_admin', 'org_admin', 'org_uploader'] },
  { to: '/batches', label: 'Batches', visibleFor: ['org_admin', 'org_uploader'] },
  { to: '/admin/batches', label: 'Validation queue', visibleFor: ['dfx_super_admin'] },
  { to: '/titles', label: 'Titles', visibleFor: ['dfx_super_admin', 'org_admin', 'org_uploader'] },
  { to: '/orgs', label: 'Organizations', visibleFor: ['dfx_super_admin'] },
  { to: '/invitations', label: 'Invitations', visibleFor: ['dfx_super_admin', 'org_admin'] },
  { to: '/spec', label: 'Spec', visibleFor: ['dfx_super_admin', 'org_admin', 'org_uploader'] },
];

export function Layout({ children }: { children: ReactNode }) {
  const { logout } = useAuth0();
  const { user } = useTransferUser();
  const location = useLocation();
  const items = NAV.filter((n) => user && n.visibleFor.includes(user.role));

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-[color:var(--color-border)] bg-[color:var(--color-bg-secondary)]">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-6">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="text-lg font-semibold tracking-tight">DFX</span>
            <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--color-text-muted)]">
              Transfer
            </span>
          </Link>
          <nav className="flex h-full items-stretch">
            {items.map((item) => {
              const active =
                item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={`relative flex items-center px-4 text-sm transition ${
                    active
                      ? 'text-[color:var(--color-text-primary)]'
                      : 'text-[color:var(--color-text-secondary)] hover:text-[color:var(--color-text-primary)]'
                  }`}
                >
                  {item.label}
                  {active ? (
                    <span
                      aria-hidden
                      className="absolute inset-x-3 bottom-0 h-[2px] bg-[color:var(--color-crimson)]"
                    />
                  ) : null}
                </Link>
              );
            })}
            <span
              aria-hidden
              className="mx-2 my-3 w-px bg-[color:var(--color-border)]"
            />
            <button
              type="button"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="flex items-center px-4 text-sm text-[color:var(--color-text-secondary)] transition hover:text-[color:var(--color-text-primary)]"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">{children}</main>
    </div>
  );
}
